import { apiClient } from '@/api/client'
import type {
  ApiKeyRecord,
  CompanyDetail,
  CreateApiKeyResult,
  RotateApiKeyResult,
  SiteAccessMode,
  SubscriptionTier,
} from './companyDetailTypes'

export async function fetchCompanyDetail(clientId: string): Promise<CompanyDetail> {
  const { data } = await apiClient.get<CompanyDetail>(`/portal/clients/api/v2/${clientId}`)
  return data
}

export async function fetchApiKeys(clientId: string): Promise<ApiKeyRecord[]> {
  const { data } = await apiClient.get<ApiKeyRecord[]>(`/portal/clients/api/v2/${clientId}/api-keys`)
  return data
}

export async function createApiKey(
  clientId: string,
  params: { name: string; subscription_tier: SubscriptionTier; site_access_mode: SiteAccessMode; controller_ids: string[] },
): Promise<CreateApiKeyResult> {
  const { data } = await apiClient.post<CreateApiKeyResult>(`/portal/clients/api/v2/${clientId}/api-keys`, params)
  return data
}

export async function revokeApiKey(clientId: string, keyId: string): Promise<void> {
  await apiClient.post(`/portal/clients/api/v2/${clientId}/api-keys/${keyId}/revoke`)
}

export async function rotateApiKey(
  clientId: string,
  keyId: string,
  gracePeriodDays: number,
): Promise<RotateApiKeyResult> {
  const { data } = await apiClient.post<RotateApiKeyResult>(
    `/portal/clients/api/v2/${clientId}/api-keys/${keyId}/rotate`,
    { grace_period_days: gracePeriodDays },
  )
  return data
}

/** PATCH .../api-keys/<key_id>/sites - change which sites an active key may
 * read (legacy company_details.html's "Sites" modal, saveSites()). The
 * backend accepts the same body legacy sent. */
export async function updateApiKeySites(
  clientId: string,
  keyId: string,
  siteAccessMode: SiteAccessMode,
  controllerIds: string[],
): Promise<void> {
  await apiClient.patch(`/portal/clients/api/v2/${clientId}/api-keys/${keyId}/sites`, {
    site_access_mode: siteAccessMode,
    controller_ids: siteAccessMode === 'selected' ? controllerIds : [],
  })
}

export async function deleteApiKey(clientId: string, keyId: string): Promise<void> {
  await apiClient.delete(`/portal/clients/api/v2/${clientId}/api-keys/${keyId}`)
}

export async function addUserToCompany(clientId: string, userId: string): Promise<void> {
  await apiClient.post(`/portal/clients/add-user/${clientId}/${userId}`)
}

export async function removeUserFromCompany(clientId: string, userId: string): Promise<void> {
  await apiClient.post(`/portal/clients/remove-user/${clientId}/${userId}`)
}

export async function addControllerToCompany(clientId: string, controllerId: string): Promise<void> {
  await apiClient.get(`/portal/clients/add-controller-to-company/${clientId}/${controllerId}`)
}

export async function removeControllerFromCompany(clientId: string, controllerId: string): Promise<void> {
  await apiClient.post(`/portal/clients/remove-controller-from-company/${clientId}/${controllerId}`)
}

export async function deleteCompany(clientId: string): Promise<void> {
  await apiClient.get(`/portal/clients/delete/${clientId}`)
}

/** New capability, not a legacy restoration - legacy's company_details.html
 * never had a way to rename a company or change its capacity after
 * creation, only create/delete. */
export async function updateCompany(
  clientId: string,
  params: { company_name?: string; capacity?: string },
): Promise<{ company_id: string; company_name: string; capacity: string | null }> {
  const { data } = await apiClient.post(`/portal/clients/update/${clientId}`, params)
  return data
}

/** Scopes a specific user to one of their company's controllers (a
 * user_controllers row) - separate from company-level attachment. `controllerId`
 * must already belong to the user's company; the backend doesn't itself
 * enforce that (neither did the legacy route this ports), so the caller's
 * "add" dropdown is what keeps the choice scoped, matching legacy's own
 * company.controllers-only picker. */
export async function addControllerToUser(userId: string, controllerId: string): Promise<void> {
  await apiClient.post(`/portal/clients/add-controller-user/${userId}/${controllerId}`)
}

export async function removeControllerFromUser(userId: string, controllerId: string): Promise<void> {
  await apiClient.get(`/portal/clients/delete-controller-user/${userId}/${controllerId}`)
}
